import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { markInterest, hasInterest, pathShowsInterest } from '../utils/engagement';

const API_URL = process.env.REACT_APP_API_URL || 'https://jambo-travel-api-4bj3.onrender.com/api';

const NewsletterPopup = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // Record safari interest as the visitor browses Safari / detail pages.
  useEffect(() => {
    if (pathShowsInterest(location.pathname)) markInterest();
  }, [location.pathname]);

  // Show the newsletter popup only AFTER the visitor has shown safari interest —
  // never on the homepage / first landing (client UX request). Re-checks on each
  // navigation; still shown at most once per browser (localStorage guard).
  useEffect(() => {
    if (localStorage.getItem('newsletterPopupShown')) return;
    if (!hasInterest()) return;
    const timer = setTimeout(() => {
      setIsOpen(true);
      localStorage.setItem('newsletterPopupShown', 'true');
    }, 8000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Close on Escape (a11y: dismissible dialog)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/email/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Subscription failed');
      }
      setIsOpen(false);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
  };

  // If popup is not open, return null
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-cafe-noir/60" role="dialog" aria-modal="true" aria-labelledby="newsletter-title">
      <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-xl p-6">
        <div className="text-center mb-6">
          <h2 id="newsletter-title" className="text-2xl font-bold text-cafe-noir mb-2">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-cafe-noir/70">
            Stay updated with our latest news and exclusive offers!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label htmlFor="newsletter-email" className="sr-only">Email address</label>
          <input
            id="newsletter-email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2 border border-french-gray rounded-md focus:outline-none focus:ring-2 focus:ring-hunyadi-yellow"
            required
            aria-required="true"
          />

          {error && (
            <p className="text-claret text-sm text-center" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col space-y-2">
            <button 
              type="submit" 
              className="w-full bg-hunyadi-yellow text-cafe-noir py-2 rounded-md hover:bg-gold-metallic transition duration-300"
            >
              Subscribe
            </button>

            <button 
              type="button" 
              onClick={() => setIsOpen(false)}
              className="w-full bg-french-gray text-cafe-noir/80 py-2 rounded-md hover:bg-french-gray transition duration-300"
            >
              No Thanks
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterPopup;